import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  EnergyBufferDataOut,
  OnDemandDetailsDataIn,
  PageInfo,
  PermissionsEnum,
} from '../../../../api/api-reference';
import { generateFileNameAppendix } from '../../../../helper/download-filename';
import { UnitConverter } from '../../../../pipes/unit-conversion';
import { AuthService } from '../../../../services/auth.service';
import { FileService } from '../../../../services/file.service';
import { TableScrollService } from '../../../../services/table-scroll.service';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';

@Component({
  selector: 'app-energy-details',
  standalone: true,
  imports: [
    TranslateModule,
    AngularSvgIconModule,
    DatePipe,
    RouterLink,
    UnitConverter,
    CommonModule,
    PaginationComponent,
  ],
  templateUrl: './energy-details.component.html',
  styleUrl: './energy-details.component.scss',
})
export class EnergyDetailsComponent implements AfterViewInit {
  data: EnergyBufferDataOut[] = [];
  commandId?: string;
  PermissionEnum = PermissionsEnum;

  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public numberOfDevices: number = 0;
  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;

  requestData = new OnDemandDetailsDataIn({
    commandId: '',
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
  });

  constructor(
    private toastr: ToastrService,
    private client: Client,
    private route: ActivatedRoute,
    public authService: AuthService,
    private fileService: FileService,
    private tableScrollService: TableScrollService,
  ) {
    this.route.queryParams.subscribe((params) => {
      this.requestData.commandId = params['commandId'];
    });
    this.loadTable(1);
  }

  @ViewChild('tableContainer') tableContainer!: ElementRef;

  ngAfterViewInit(): void {
    this.initializeTableEvents();
  }

  private initializeTableEvents(): void {
    const tableContainer = this.tableContainer.nativeElement;
    // Drag events
    tableContainer.addEventListener('mousedown', (event: MouseEvent) =>
      this.tableScrollService.startDrag(event, tableContainer),
    );
    tableContainer.addEventListener('mousemove', (event: MouseEvent) =>
      this.tableScrollService.onDrag(event, tableContainer),
    );
    tableContainer.addEventListener('mouseup', () =>
      this.tableScrollService.stopDrag(tableContainer),
    );
    tableContainer.addEventListener('mouseleave', () =>
      this.tableScrollService.stopDrag(tableContainer),
    );
    // Wheel event for horizontal scroll
    tableContainer.addEventListener('wheel', (event: WheelEvent) =>
      this.tableScrollService.handleHorizontalScroll(event, tableContainer),
    );
  }

  loadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.getOnDemandEnergyRegisters(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.loadTable(this.currentPage);
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    this.client.downloadOnDemandEnergyRegisters(this.requestData.commandId).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `EnergyRegister${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
